/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Sep 20 2024
 **/
import styled from "@emotion/styled";

import {
	Avatar,
	Button,
	Column,
	Divider,
	Row,
	Typography,
	TypographyCSS,
	useToast
} from "@/frameworks/designStuff/components";

import { _T, _P } from "@rfice/language";

import { useSpace } from "@/frameworks/core/API";
import { usePopover } from "@/frameworks/core/hooks";
import * as S from "../private/emotion";
import { POPOVER_ID } from "@/frameworks/core/commons";

export default function SpaceJoinRequestPopup({
	spaceInfo,
	userInfo,
	onClose
}: {
	spaceInfo: PushServices.Space.SpaceJoinRequest.Data["spaceInfo"];
	userInfo: PushServices.Space.SpaceJoinRequest.Data["userInfo"];
	onClose: () => void;
}) {
	const popover = usePopover();
	const spaceAPI = useSpace();
	const toast = useToast();

	const handleApprove = async () => {
		const params = { userIds: [userInfo?.id], approve: true };
		await spaceAPI.join.process.execute({ spaceId: spaceInfo.id, ...params });
		toast.show({
			type: "system",
			message: _T("수락했습니다.")
		});
		popover.hide(POPOVER_ID.BACKGROUND_UI_POPUP);
	};
	const handleDecline = () => {
		const params = { userIds: [userInfo?.id], approve: false };
		spaceAPI.join.process.execute({ spaceId: spaceInfo.id, ...params });
		toast.show({
			type: "system",
			message: _T("거절했습니다.")
		});
		popover.hide(POPOVER_ID.BACKGROUND_UI_POPUP);
	};

	return (
		<S.NoticeWrapper gap={12}>
			<Row css={{ position: "absolute", right: "20px", top: "8px" }}>
				<Button.Icon name="Close24" filled="transparent" variant="contained" size="medium" onClick={onClose} />
			</Row>
			<Row gap={16}>
				<Avatar
					kind="space_80"
					type="image"
					text={spaceInfo.name ?? ""}
					textColor="deco1_50_100"
					src={spaceInfo.thumbnailPath}
					css={{
						flex: "none"
					}}
				/>
				<Column gap={8}>
					<Typography typoSize="Head6">{spaceInfo.name}</Typography>
					<Column>
						<UserName typoSize="Body3_MD">
							{_P("{{name}} 님이 스페이스 가입을 요청하였습니다.", {
								name: `<span id='target'>${userInfo.name}</span>`
							})}
						</UserName>
					</Column>
				</Column>
			</Row>
			<Divider type="line" />
			<Row style={{ width: "100%" }} horizonAlign="right" gap={8}>
				<Button filled="secondary" onClick={handleDecline}>
					{_T("거절")}
				</Button>
				<Button filled="primary" onClick={handleApprove}>
					{_T("수락")}
				</Button>
			</Row>
		</S.NoticeWrapper>
	);
}

const UserName = styled(Typography)`
	#target {
		${TypographyCSS("Title3")}
	}
	white-space: pre-wrap;
`;
